import { apiRequest, queryClient } from '@/lib/queryClient';
import type { SeatMoveBody } from '@shared/validation/seatFirst';
import type { TravelTier } from '@shared/geo/travelTier';
import { seatKeys } from '@/lib/seatFirstApi';

export interface BoardSeatGame {
  gameId: string;
  status: string;
  date: string | null;
  time: string | null;
  diamondName: string | null;
  opponentSeatId: string;
  isHome: boolean;
}

export interface BoardSeat {
  id: string;
  name: string;
  poolId: string;
  poolName: string;
  seatNumber: number;
  travelFriendly: boolean;
  occupant: { id: string; name: string } | null;
  games: BoardSeatGame[];
}

export interface SeatBoardResponse {
  divisionId: string;
  seatBuildStatus: 'NONE' | 'BUILDING' | 'BUILT' | 'FINAL';
  scheduleVersion: number;
  pools: Array<{ id: string; name: string; seatIds: string[] }>;
  seats: BoardSeat[];
}

export interface TrayTeam {
  id: string;
  name: string;
  tier: TravelTier | null;
  isProtected: boolean;
  distanceKm: number | null;
  hasDirectGames: boolean;
}

export interface SeatTrayResponse {
  teams: TrayTeam[];
  occupants: Array<{ teamId: string; tier: TravelTier | null; isProtected: boolean }>;
}

export interface SeatMoveResponse {
  action: SeatMoveBody['action'];
  divisionId: string;
  scheduleVersion: number;
  seatIds: string[];
  teamIds: string[];
  publishedGamesAffected: number;
}

export interface SeatSuggestion {
  seatId: string;
  seatName: string;
  action: 'fill' | 'move' | 'swap';
}

export interface SeatMoveErrorBody {
  error: string;
  code?: string;
  publishedGames?: number;
  suggestions?: SeatSuggestion[];
}

export const seatBoardKeys = {
  tray: (tournamentId: string, divisionId: string) =>
    [...seatKeys.board(tournamentId, divisionId), 'tray'] as const,
};

export async function postSeatMove(
  tournamentId: string,
  body: SeatMoveBody
): Promise<SeatMoveResponse> {
  const res = await apiRequest('POST', `/api/tournaments/${tournamentId}/seats/move`, body);
  return (await res.json()) as SeatMoveResponse;
}

/** Reads the JSON body `apiRequest` attaches to a failed request. */
export function readSeatMoveError(err: unknown): SeatMoveErrorBody | null {
  if (!(err instanceof Error)) return null;
  const body = (err as Error & { body?: unknown }).body;
  if (!body || typeof body !== 'object' || typeof (body as { error?: unknown }).error !== 'string')
    return null;
  return body as SeatMoveErrorBody;
}

/** The board prefix also covers the tray; teams and games change because seat games are re-pointed. */
export function invalidateSeatBoard(tournamentId: string, divisionId: string): void {
  void queryClient.invalidateQueries({ queryKey: seatKeys.board(tournamentId, divisionId) });
  void queryClient.invalidateQueries({ queryKey: ['/api/tournaments', tournamentId, 'teams'] });
  void queryClient.invalidateQueries({ queryKey: ['/api/tournaments', tournamentId, 'games'] });
  void queryClient.invalidateQueries({ queryKey: seatKeys.settings(tournamentId) });
}

/** The inverse action for undo, given the board as it was before the move. */
export function inverseSeatMove(
  body: SeatMoveBody,
  before: SeatBoardResponse,
  scheduleVersion: number
): SeatMoveBody | null {
  const base = {
    expectedScheduleVersion: scheduleVersion,
    confirmPublished: body.confirmPublished,
  };
  switch (body.action) {
    case 'fill':
      return { action: 'release', seatId: body.seatId, ...base };
    case 'release': {
      const team = before.seats.find(s => s.id === body.seatId)?.occupant;
      return team
        ? { action: 'fill', seatId: body.seatId, teamId: team.id, overrideTravel: true, ...base }
        : null;
    }
    case 'move':
      return {
        action: 'move',
        fromSeatId: body.toSeatId,
        toSeatId: body.fromSeatId,
        overrideTravel: true,
        ...base,
      };
    case 'swap':
      return {
        action: 'swap',
        seatAId: body.seatAId,
        seatBId: body.seatBId,
        overrideTravel: true,
        ...base,
      };
  }
}

/** Optimistic board/tray after a move (no game slots change, only occupants). */
export function applySeatMoveLocally(
  board: SeatBoardResponse,
  tray: SeatTrayResponse | undefined,
  body: SeatMoveBody
): { board: SeatBoardResponse; tray: SeatTrayResponse | undefined } {
  const occupants = new Map(board.seats.map(s => [s.id, s.occupant]));
  let trayTeams = tray?.teams ?? [];
  const toTray = (o: { id: string; name: string } | null) => {
    if (!o) return;
    const travel = tray?.occupants.find(t => t.teamId === o.id);
    trayTeams = [
      ...trayTeams,
      {
        id: o.id,
        name: o.name,
        tier: travel?.tier ?? null,
        isProtected: travel?.isProtected ?? false,
        distanceKm: null,
        hasDirectGames: false,
      },
    ];
  };
  if (body.action === 'fill') {
    const team = trayTeams.find(t => t.id === body.teamId);
    occupants.set(body.seatId, team ? { id: team.id, name: team.name } : null);
    trayTeams = trayTeams.filter(t => t.id !== body.teamId);
  } else if (body.action === 'release') {
    toTray(occupants.get(body.seatId) ?? null);
    occupants.set(body.seatId, null);
  } else {
    const [a, b] =
      body.action === 'move' ? [body.fromSeatId, body.toSeatId] : [body.seatAId, body.seatBId];
    const oa = occupants.get(a) ?? null;
    occupants.set(a, occupants.get(b) ?? null);
    occupants.set(b, oa);
  }
  return {
    board: {
      ...board,
      seats: board.seats.map(s => ({ ...s, occupant: occupants.get(s.id) ?? null })),
    },
    tray: tray ? { ...tray, teams: trayTeams.sort((x, y) => x.name.localeCompare(y.name)) } : tray,
  };
}
